import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/Login.css';
import axios from 'axios';

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    // Extract query params
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const email = queryParams.get('email');

    useEffect(() => {
        if (!token || !email) {
            setError('Invalid password reset link.');
        }
    }, [token, email]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== passwordConfirmation) {
            return setError("Passwords do not match.");
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/i;
        if (!passwordRegex.test(password)) {
            return setError("Password must be at least 8 characters and contain at least one uppercase letter, one number, and one symbol.");
        }

        setLoading(true);
        setError('');
        setMessage('');

        try {
            const response = await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/reset-password`, {
                token,
                email,
                password,
                password_confirmation: passwordConfirmation
            });
            setMessage(response.data.message || 'Password has been reset successfully!');
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id="auth-page">
            {/* Floating Shapes */}
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>

            <div className="auth-card">
                <div className="icon-container">
                    <img src="/logo.svg" alt="LMS Logo" className="logo-img" />
                </div>

                <div className="auth-header text-center mb-4">
                    <h3>Reset Password</h3>
                    <p className="text-muted">Enter your new password below.</p>
                </div>

                {message && <div className="alert alert-success py-2 small">{message}</div>}
                {error && <div className="alert alert-danger py-2 small">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="mb-3 text-start">
                        <label className="form-label fw-bold text-muted small">New Password</label>
                        <div className="input-group">
                            <span className="input-group-text"><i className="bi bi-lock"></i></span>
                            <input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                placeholder="Min. 8 characters"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength="8"
                            />
                            <button
                                type="button"
                                className="input-group-text bg-white"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ borderLeft: 'none', cursor: 'pointer' }}
                            >
                                <i className={`bi ${showPassword ? 'bi-eye-slash text-muted' : 'bi-eye text-primary'}`}></i>
                            </button>
                        </div>
                        <small className="text-muted d-block mt-1 text-nowrap" style={{ fontSize: '0.75rem' }}>
                            Min 8 chars, 1 uppercase, 1 number, & 1 symbol.
                        </small>
                    </div>

                    <div className="mb-4 text-start">
                        <label className="form-label fw-bold text-muted small">Confirm Password</label>
                        <div className="input-group">
                            <span className="input-group-text"><i className="bi bi-lock"></i></span>
                            <input
                                type={showPasswordConfirmation ? "text" : "password"}
                                className="form-control"
                                placeholder="Confirm your new password"
                                value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className="input-group-text bg-white"
                                onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                                style={{ borderLeft: 'none', cursor: 'pointer' }}
                            >
                                <i className={`bi ${showPasswordConfirmation ? 'bi-eye-slash text-muted' : 'bi-eye text-primary'}`}></i>
                            </button>
                        </div>
                    </div>

                    <button type="submit" className="btn btn-auth" disabled={loading || !token || !email}>
                        {loading ? (
                            <span><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Resetting...</span>
                        ) : (
                            'Reset Password'
                        )}
                    </button>
                </form>

                <div className="mt-4 text-center">
                    <small className="text-muted">Remember your password? <a href="/login" className="text-primary fw-bold hover-underline">Login</a></small>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
